import type { FC, MouseEvent } from 'react';
import { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { captureException } from '@sentry/react';
import { Avatar, Col, Input, Row, Space, Switch } from 'antd';
import { TbFileText, TbSearch, TbTopologyStar3 } from 'react-icons/tb';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useDismissibleSelection } from '@/hooks/useDismissibleSelection';
import { memoService } from '@/services';
import type { DocumentConceptReference } from '@/shared/components/attachments/AttachmentRedactionEditor/documentIntelligence';
import { ResourceSiderToggleRow } from '@/shared/components/filters/SearchSiderToggleRow';
import OverflowAwareScrollEndFooter from '@/shared/components/layouts/footers/OverflowAwareScrollEndFooter';
import ResourceHeader from '@/shared/components/layouts/headers/ResourceHeader';
import OverflowFadeScrollArea from '@/shared/components/layouts/OverflowFadeScrollArea';
import EmptySection from '@/shared/components/layouts/views/EmptySection';
import QuoteMemoDropdownPanel from '@/shared/components/memos/QuoteMemoDropdownPanel';
import { BaseSkeleton } from '@/shared/components/skeletons';
import { toast } from '@/shared/components/toasts/PlatformToast';
import SmallTooltip from '@/shared/components/tooltips/SmallTooltip';
import { MiddleDot } from '@/shared/components/typography';
import { useMemoStore } from '@/stores';
import type { ContextualCommentTarget, Memo } from '@/types';
import { dayjs, resolveCorporateLogo } from '@/utilities';

import TranscriptSectionCard from './components/TranscriptSectionCard';
import { TranscriptDetailsSider, TranscriptListSider, TranscriptSelectionToolbar } from './TranscriptWorkspacePanels';
import type {
    TranscriptDetailsTab,
    TranscriptOrdering,
    TranscriptPeriod,
    TranscriptPeriodFilter,
    TranscriptQuoteSelection,
    TranscriptSection,
    TranscriptSourceIdentity,
    TranscriptWorkspaceProps,
} from './types';
import useTranscriptOntology from './useTranscriptOntology';
import { useTranscriptWorkspaceData } from './useTranscriptWorkspaceData';
import {
    buildTranscriptOntologyAnnotations,
    buildTranscriptSourceUrl,
    getSelectionOffsets,
    getTranscriptLabel,
    getTranscriptQuoteHtml,
    getTranscriptSpeakerTitle,
    getTranscriptSpeakerTitles,
    memoReferencesTranscript,
} from './utils';

const TranscriptWorkspace: FC<TranscriptWorkspaceProps> = ({
    commentResource,
    emptyDescription = 'Earnings transcript data is not available for this company.',
    sourceEntity,
    symbol,
}) => {
    const { token } = useAntDesignTheme();
    const location = useLocation();
    const navigate = useNavigate();
    const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const initialQuarter = Number(searchParams.get('quarter')) || undefined;
    const initialYear = Number(searchParams.get('year')) || undefined;
    const sourceSectionId = searchParams.get('section') ?? undefined;
    const sourceStart = Number(searchParams.get('start'));
    const sourceEnd = Number(searchParams.get('end'));
    const {
        activePeriod,
        executives,
        loadingPeriods,
        loadingTranscript,
        periods,
        sections,
        setActivePeriod,
        transcriptText,
    } = useTranscriptWorkspaceData({ initialQuarter, initialYear, symbol });
    const { createMemo, fetchMemos, memos, updateMemo } = useMemoStore();
    const [leftSiderOpen, setLeftSiderOpen] = useState(true);
    const [rightSiderOpen, setRightSiderOpen] = useState(true);
    const [periodQuery, setPeriodQuery] = useState('');
    const [conversationQuery, setConversationQuery] = useState('');
    const [periodFilter, setPeriodFilter] = useState<TranscriptPeriodFilter>('all');
    const [periodOrdering, setPeriodOrdering] = useState<TranscriptOrdering>('newest');
    const [showPeriodDates, setShowPeriodDates] = useState(true);
    const [ontologyEnabled, setOntologyEnabled] = useState(false);
    const [rightTab, setRightTab] = useState<TranscriptDetailsTab>('highlights');
    const [selection, setSelection] = useState<TranscriptQuoteSelection>();
    const deferredPeriodQuery = useDeferredValue(periodQuery);
    const deferredConversationQuery = useDeferredValue(conversationQuery);
    const readerScrollRef = useRef<HTMLDivElement>(null);
    const readerContentRef = useRef<HTMLDivElement>(null);
    const {
        activeReferenceId: activeOntologyReferenceId,
        error: ontologyError,
        intelligence: ontologyIntelligence,
        loading: ontologyLoading,
        setActiveReferenceId: setActiveOntologyReferenceId,
    } = useTranscriptOntology({ enabled: ontologyEnabled, text: transcriptText });

    useDismissibleSelection({ active: Boolean(selection), onDismiss: useCallback(() => setSelection(undefined), []) });

    useEffect(() => {
        void fetchMemos();
    }, [fetchMemos]);

    useEffect(() => {
        if (loadingTranscript || !sourceSectionId) {
            return;
        }

        window.requestAnimationFrame(() => {
            const target = readerContentRef.current?.querySelector<HTMLElement>(
                '[data-transcript-source-target="true"]',
            );
            const section = readerContentRef.current?.querySelector<HTMLElement>(`#${CSS.escape(sourceSectionId)}`);

            (target ?? section)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
    }, [loadingTranscript, sections.length, sourceSectionId]);

    const identity = useMemo<TranscriptSourceIdentity | undefined>(
        () => (activePeriod ? { quarter: activePeriod.quarter, symbol, year: activePeriod.year } : undefined),
        [activePeriod, symbol],
    );
    const transcriptComponent = identity ? `transcript-${identity.symbol}-${identity.year}-q${identity.quarter}` : '';
    const indexedPeriods = useMemo(
        () =>
            periods.map((period) => ({
                period,
                searchText: `${getTranscriptLabel(period)} ${
                    period.date && dayjs(period.date).isValid() ? dayjs(period.date).format('MMM D YYYY') : ''
                }`.toLocaleLowerCase(),
                sortValue:
                    period.date && dayjs(period.date).isValid()
                        ? dayjs(period.date).valueOf()
                        : period.year * 10 + period.quarter,
            })),
        [periods],
    );
    const filteredPeriods = useMemo(() => {
        const query = deferredPeriodQuery.trim().toLocaleLowerCase();
        const requestedQuarter = periodFilter === 'all' ? undefined : Number(periodFilter.slice(1));

        return indexedPeriods
            .filter(
                ({ period, searchText }) =>
                    (!requestedQuarter || period.quarter === requestedQuarter) &&
                    (!query || searchText.includes(query)),
            )
            .sort((left, right) =>
                periodOrdering === 'newest' ? right.sortValue - left.sortValue : left.sortValue - right.sortValue,
            )
            .map(({ period }) => period);
    }, [deferredPeriodQuery, indexedPeriods, periodFilter, periodOrdering]);
    const indexedSections = useMemo(
        () =>
            sections.map((section) => ({
                searchText: `${section.speaker} ${section.content}`.toLocaleLowerCase(),
                section,
            })),
        [sections],
    );
    const visibleSections = useMemo(() => {
        const query = deferredConversationQuery.trim().toLocaleLowerCase();

        return query
            ? indexedSections.filter(({ searchText }) => searchText.includes(query)).map(({ section }) => section)
            : sections;
    }, [deferredConversationQuery, indexedSections, sections]);
    const speakerTitles = useMemo(() => getTranscriptSpeakerTitles(sections, executives), [executives, sections]);
    const ontologyAnnotations = useMemo(
        () => buildTranscriptOntologyAnnotations(sections, ontologyIntelligence),
        [ontologyIntelligence, sections],
    );
    const linkedMemos = useMemo(
        () => (identity ? memos.filter((memo) => memoReferencesTranscript(memo, identity)) : []),
        [identity, memos],
    );

    const selectPeriod = (period: TranscriptPeriod) => {
        setSelection(undefined);
        setConversationQuery('');
        setActivePeriod(period);
        const nextSearchParams = new URLSearchParams(location.search);
        nextSearchParams.set('year', String(period.year));
        nextSearchParams.set('quarter', String(period.quarter));
        nextSearchParams.delete('section');
        nextSearchParams.delete('start');
        nextSearchParams.delete('end');
        navigate({ pathname: location.pathname, search: nextSearchParams.toString() }, { replace: true });
    };

    const handleTextSelection = (event: MouseEvent<HTMLElement>, section: TranscriptSection) => {
        const browserSelection = window.getSelection();

        if (!browserSelection || browserSelection.isCollapsed || browserSelection.rangeCount === 0) {
            return;
        }

        const range = browserSelection.getRangeAt(0);
        const offsets = getSelectionOffsets(event.currentTarget, range);
        const contentContainer = readerContentRef.current;

        if (!offsets || !contentContainer) {
            return;
        }

        const rangeRect = range.getBoundingClientRect();
        const containerRect = contentContainer.getBoundingClientRect();
        const estimatedToolbarWidth = 19 * 16;

        setSelection({
            end: section.start + offsets.end,
            left: Math.max(
                12,
                Math.min(rangeRect.left - containerRect.left, containerRect.width - estimatedToolbarWidth),
            ),
            quote: offsets.quote,
            sectionId: section.id,
            speaker: section.speaker,
            start: section.start + offsets.start,
            top: rangeRect.bottom - containerRect.top + 8,
        });
        setRightTab('highlights');
    };

    const sourceUrl = useCallback(
        (quoteSelection: TranscriptQuoteSelection) =>
            identity
                ? buildTranscriptSourceUrl({ identity, pathname: location.pathname, selection: quoteSelection })
                : '',
        [identity, location.pathname],
    );
    const quoteHtml = useCallback(
        (quoteSelection: TranscriptQuoteSelection) =>
            identity
                ? getTranscriptQuoteHtml({
                      identity,
                      quote: quoteSelection.quote,
                      sourceEntity,
                      sourceUrl: sourceUrl(quoteSelection),
                  })
                : '',
        [identity, sourceEntity, sourceUrl],
    );

    const copyQuote = useCallback(
        async (quoteSelection: TranscriptQuoteSelection) => {
            try {
                await navigator.clipboard.writeText(`${quoteSelection.quote}\n\nSource: ${sourceUrl(quoteSelection)}`);
                toast.success('Transcript quote copied');
            } catch (error) {
                captureException(error);
                toast.error('Transcript quote could not be copied');
            }
        },
        [sourceUrl],
    );

    const copyQuoteLink = useCallback(
        async (quoteSelection: TranscriptQuoteSelection) => {
            try {
                await navigator.clipboard.writeText(sourceUrl(quoteSelection));
                toast.success('Transcript passage link copied');
            } catch (error) {
                captureException(error);
                toast.error('Transcript passage link could not be copied');
            }
        },
        [sourceUrl],
    );

    const createMemoFromQuote = useCallback(
        async (quoteSelection: TranscriptQuoteSelection) => {
            if (!identity) {
                return;
            }

            try {
                const memo = await createMemo({
                    body: quoteHtml(quoteSelection),
                    title: `${identity.symbol} ${getTranscriptLabel(identity)} Notes`,
                });

                if (memo?.id) {
                    toast.success('Memo created from transcript quote');
                    navigate(`/memos/${memo.id}`);
                }
            } catch (error) {
                captureException(error);
                toast.error('Failed to create Memo from transcript quote');
            }
        },
        [createMemo, identity, navigate, quoteHtml],
    );

    const appendQuoteToMemo = useCallback(
        async (memo: Memo, quoteSelection: TranscriptQuoteSelection) => {
            try {
                const fullMemo = await memoService.getById(memo.id);
                const existingBody = fullMemo.body ?? memo.body ?? '';
                const nextBody = `${existingBody.trim() ? `${existingBody}<p></p>` : ''}${quoteHtml(quoteSelection)}`;

                await updateMemo(memo.id, { body: nextBody });
                toast.success('Transcript quote added to Memo', {
                    description: fullMemo.title || memo.title || 'Untitled Memo',
                });
            } catch (error) {
                captureException(error);
                toast.error('Failed to add transcript quote to Memo');
            }
        },
        [quoteHtml, updateMemo],
    );

    const quoteMemoPanel = selection ? (
        <QuoteMemoDropdownPanel
            memos={memos}
            onCopy={() => copyQuote(selection)}
            onCreate={() => createMemoFromQuote(selection)}
            onSelectMemo={(memo) => appendQuoteToMemo(memo, selection)}
        />
    ) : (
        <QuoteMemoDropdownPanel
            active={false}
            memos={memos}
            onCopy={() => undefined}
            onCreate={() => undefined}
            onSelectMemo={() => undefined}
        />
    );
    const selectionCommentTarget = useMemo<ContextualCommentTarget | undefined>(
        () =>
            selection && identity
                ? {
                      component: transcriptComponent,
                      label: `${selection.speaker} · ${getTranscriptLabel(identity)}`,
                      metadata: {
                          breadcrumbs: [getTranscriptLabel(identity), selection.speaker],
                          selection: {
                              end: selection.end,
                              kind: 'text-selection',
                              quote: selection.quote,
                              sectionId: selection.sectionId,
                              start: selection.start,
                          },
                          source: 'transcript-selection',
                      },
                      type: 'text-selection',
                  }
                : undefined,
        [identity, selection, transcriptComponent],
    );

    const setOntologyMode = useCallback(
        (enabled: boolean) => {
            setOntologyEnabled(enabled);

            if (enabled) {
                setRightSiderOpen(true);
                setRightTab('ontology');
            } else if (rightTab === 'ontology') {
                setRightTab('highlights');
            }
        },
        [rightTab],
    );
    const selectOntologyReference = useCallback(
        (reference: DocumentConceptReference) => {
            setOntologyEnabled(true);
            setRightSiderOpen(true);
            setRightTab('ontology');
            setActiveOntologyReferenceId(reference.id);

            const section = sections.find(
                (candidate) => reference.textStart >= candidate.start && reference.textStart < candidate.end,
            );

            if (section) {
                window.requestAnimationFrame(() => {
                    readerContentRef.current
                        ?.querySelector<HTMLElement>(`#${CSS.escape(section.id)}`)
                        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                });
            }
        },
        [sections, setActiveOntologyReferenceId],
    );
    const ontologyReferenceLocationLabel = useCallback(
        (reference: DocumentConceptReference) =>
            sections.find((section) => reference.textStart >= section.start && reference.textStart < section.end)
                ?.speaker ?? 'Transcript',
        [sections],
    );

    const activeLabel = activePeriod ? getTranscriptLabel(activePeriod) : 'Transcript';
    const activeDate =
        activePeriod?.date && dayjs(activePeriod.date).isValid()
            ? dayjs(activePeriod.date).format('MMM D, YYYY h:mm A')
            : undefined;
    const resolvedLogo = resolveCorporateLogo(sourceEntity?.website, sourceEntity?.logoUrl);

    return (
        <Row style={{ background: token.colorBgContainer, height: '100%', minHeight: 0 }} wrap={false}>
            <TranscriptListSider
                activePeriod={activePeriod}
                emptyDescription={emptyDescription}
                filter={periodFilter}
                loading={loadingPeriods}
                ordering={periodOrdering}
                showDates={showPeriodDates}
                onFilterChange={setPeriodFilter}
                onOrderingChange={setPeriodOrdering}
                onQueryChange={setPeriodQuery}
                onSelectPeriod={selectPeriod}
                onShowDatesChange={setShowPeriodDates}
                open={leftSiderOpen}
                periods={filteredPeriods}
                query={periodQuery}
            />
            <Col flex='auto' style={{ minWidth: 0 }}>
                <Row style={{ height: '100%' }} wrap={false}>
                    <Col flex='auto' style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                        <ResourceHeader
                            breadcrumbs={[
                                {
                                    icon: <TbFileText style={{ color: token.colorTextTertiary }} />,
                                    label: 'Transcripts',
                                },
                                { label: activeLabel },
                            ]}
                            rightActions={
                                <small
                                    style={{
                                        alignItems: 'center',
                                        color: token.colorTextTertiary,
                                        display: 'inline-flex',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    <span style={{ alignItems: 'center', display: 'inline-flex', gap: '0.375rem' }}>
                                        <Avatar size={20} src={resolvedLogo}>
                                            {sourceEntity?.name?.[0] ?? symbol[0]}
                                        </Avatar>
                                        {sourceEntity?.name || symbol}
                                    </span>
                                    {activeDate ? <MiddleDot horizontalMargin='0.375rem' /> : null}
                                    {activeDate}
                                </small>
                            }
                            style={{ flex: '0 0 auto', padding: '1rem 1.5rem' }}
                        />
                        <ResourceSiderToggleRow
                            ariaLabel='Transcript layout controls'
                            leftSiderCollapsed={!leftSiderOpen}
                            leftSiderLabel='Transcript list'
                            onToggleLeftSider={() => setLeftSiderOpen((current) => !current)}
                            onToggleRightSider={() => setRightSiderOpen((current) => !current)}
                            rightSiderAvailable
                            rightSiderCollapsed={!rightSiderOpen}
                            rightSiderLabel='Transcript details'
                            style={{ height: '3rem', margin: 0, minHeight: '3rem' }}
                        >
                            <Row align='middle' justify='end' style={{ height: '3rem', padding: '0 0.5rem' }}>
                                <Space align='center' size='small'>
                                    <Input
                                        allowClear
                                        aria-label='Search transcript conversation'
                                        placeholder='Search Conversation...'
                                        prefix={
                                            <TbSearch size='0.875rem' style={{ color: token.colorTextQuaternary }} />
                                        }
                                        size='small'
                                        style={{ width: '20rem' }}
                                        value={conversationQuery}
                                        onChange={(event) => setConversationQuery(event.target.value)}
                                    />
                                    <SmallTooltip
                                        showInfo={false}
                                        title={`${ontologyEnabled ? 'Disable' : 'Enable'} Transcript Ontology Mode`}
                                    >
                                        <Switch
                                            aria-label='Transcript Ontology Mode'
                                            checked={ontologyEnabled}
                                            checkedChildren={<TbTopologyStar3 />}
                                            loading={ontologyEnabled && ontologyLoading}
                                            size='small'
                                            unCheckedChildren={<TbTopologyStar3 />}
                                            onChange={setOntologyMode}
                                        />
                                    </SmallTooltip>
                                </Space>
                            </Row>
                        </ResourceSiderToggleRow>
                        <OverflowFadeScrollArea
                            aria-label={activeLabel}
                            containerStyle={{ flex: '1 1 auto', minHeight: 0 }}
                            ref={readerScrollRef}
                            surface='container'
                        >
                            <div
                                ref={readerContentRef}
                                style={{
                                    margin: '0 auto',
                                    maxWidth: '64rem',
                                    padding: '1.25rem',
                                    position: 'relative',
                                }}
                            >
                                {loadingTranscript ? (
                                    <BaseSkeleton active paragraph={{ rows: 24 }} />
                                ) : visibleSections.length ? (
                                    visibleSections.map((section) => {
                                        const sectionSelection: TranscriptQuoteSelection = {
                                            end: section.end,
                                            left: 0,
                                            quote: section.content,
                                            sectionId: section.id,
                                            speaker: section.speaker,
                                            start: section.start,
                                            top: 0,
                                        };
                                        const sectionMemoPanel = (
                                            <QuoteMemoDropdownPanel
                                                memos={memos}
                                                onCopy={() => copyQuote(sectionSelection)}
                                                onCreate={() => createMemoFromQuote(sectionSelection)}
                                                onSelectMemo={(memo) => appendQuoteToMemo(memo, sectionSelection)}
                                            />
                                        );

                                        return (
                                            <TranscriptSectionCard
                                                activeOntologyReferenceId={activeOntologyReferenceId}
                                                annotations={ontologyAnnotations.get(section.id) ?? []}
                                                executiveTitle={getTranscriptSpeakerTitle(
                                                    speakerTitles,
                                                    section.speaker,
                                                )}
                                                key={section.id}
                                                memoPanel={sectionMemoPanel}
                                                ontologyEnabled={ontologyEnabled}
                                                section={section}
                                                sourceEnd={sourceEnd}
                                                sourceSectionId={sourceSectionId}
                                                sourceStart={sourceStart}
                                                onCopy={() => void copyQuote(sectionSelection)}
                                                onCopyLink={() => void copyQuoteLink(sectionSelection)}
                                                onCreateMemo={() => void createMemoFromQuote(sectionSelection)}
                                                onOpenOntology={() => setOntologyMode(true)}
                                                onOpenReferences={() => {
                                                    setRightSiderOpen(true);
                                                    setRightTab('references');
                                                }}
                                                onReferenceSelect={selectOntologyReference}
                                                onTextSelection={(event) => handleTextSelection(event, section)}
                                            />
                                        );
                                    })
                                ) : (
                                    <EmptySection
                                        description={
                                            conversationQuery
                                                ? 'No speakers or transcript passages match your search.'
                                                : 'A transcript is not available for this earnings call.'
                                        }
                                        height='20rem'
                                        icon={<TbFileText size='1.25rem' style={{ color: token.colorTextTertiary }} />}
                                        label={
                                            conversationQuery ? 'No Matching Conversation' : 'Transcript Unavailable'
                                        }
                                    />
                                )}
                                {!loadingTranscript && visibleSections.length ? (
                                    <OverflowAwareScrollEndFooter label='End of Transcript' />
                                ) : null}
                                {selection ? (
                                    <TranscriptSelectionToolbar
                                        commentResource={commentResource}
                                        commentTarget={selectionCommentTarget}
                                        memoPanel={quoteMemoPanel}
                                        onCopy={() => {
                                            if (selection) {
                                                void copyQuote(selection);
                                            }
                                        }}
                                        onCreateMemo={() => {
                                            if (selection) {
                                                void createMemoFromQuote(selection);
                                            }
                                        }}
                                        selection={selection}
                                    />
                                ) : null}
                            </div>
                        </OverflowFadeScrollArea>
                    </Col>
                    <TranscriptDetailsSider
                        activeOntologyReferenceId={activeOntologyReferenceId}
                        linkedMemos={linkedMemos}
                        ontologyEnabled={ontologyEnabled}
                        ontologyError={ontologyError}
                        ontologyIntelligence={ontologyIntelligence}
                        ontologyLoading={ontologyLoading}
                        ontologyReferenceLocationLabel={ontologyReferenceLocationLabel}
                        onOntologyReferenceSelect={selectOntologyReference}
                        onTabChange={setRightTab}
                        open={rightSiderOpen}
                        selection={selection}
                        tab={rightTab}
                    />
                </Row>
            </Col>
        </Row>
    );
};

export default TranscriptWorkspace;
export type { TranscriptWorkspaceProps } from './types';
